import { Button, Col, Form, Input, message, Modal, PageHeader } from "antd";
import LayoutCss from "layout/layout.module.scss";
import { useEffect, useRef, useState } from "react";
import { PatternFormat } from "react-number-format";
import { useSelector } from "react-redux";
import ClientContact from "redux/models/clientContacts";
import { selectUser, selectUserId } from "redux/slices/userSlice";
import K from "utilities/constants";
import {
  noTrailingSpaceAllowedRule,
  validateMobileNumber,
} from "utilities/generalUtility";
import { DataTableLayout } from "common/components/dataTableLayout/dataTableLayout";
import { ClientSideDataGrid } from "common/components/clientSideDataGrid/clientSideDataGrid";
import { usePageFilter } from "hooks/usePageFilters";
import { ExportDataButton } from "common/components/ExportDataButton";
import Export from "redux/models/export";
import { getColumns } from "./columns";

export default function ClientContacts() {
  const userId = useSelector(selectUserId);
  const userSlice = useSelector(selectUser);

  const [search, setSearch] = usePageFilter("client_contact_search");

  const gridRef = useRef(null);

  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [editData, setEditData] = useState({});
  const [clientContacts, setClientContacts] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    form.submit();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const editRecord = (record) => {
    setEditData(record);
    showModal();
  };

  const afterModalClose = () => {
    setEditData({});
  };

  const confirmDelete = async (id) => {
    try {
      console.log(id);
      await ClientContact.deleteClientContact(id);
      setClientContacts((prev) => prev.filter((item) => item.id !== id));
      setIsModalVisible(false);
      message.success("Client contact deleted successfully");
    } catch (err) {
      console.error(err);
    }
  };

  const onFinish = async (values) => {
    setIsLoading(true);
    if (Object.keys(editData).length) {
      try {
        const res = await ClientContact.update(values, editData.id);
        setClientContacts(
          clientContacts.map((item) => {
            if (item.id === res.id)
              return res;
            return item;
          }),
        );
        setIsLoading(false);
        setIsModalVisible(false);
        form.resetFields();
        message.success("Client updated successfully");
      } catch (err) {
        setIsLoading(false);
        console.error(err);
      }
      setEditData({});
    } else {
      try {
        const created = await ClientContact.create(values);
        setClientContacts([...clientContacts, created]);
        setIsLoading(false);
        setIsModalVisible(false);
        form.resetFields();
        message.success("Client created successfully");
      } catch (err) {
        setIsLoading(false);
        console.error(err);
      }
    }
  };

  const getAllContacts = async () => {
    try {
      const res = await ClientContact.getAll();
      setClientContacts(res);
    } catch (err) {
      console.error(err);
    }
  };

  const onExportRequested = async () => {
    try {
      const { columnApi } = gridRef.current;
      const sortColumn = columnApi.getColumnState().find((c) => c.sort);

      const params = {
        search: search ?? undefined,
        sortBy: sortColumn?.colId,
        sortOrder: sortColumn?.sort,
      };

      await Export.getReportClientContacts(params);

      message.success(
        "Client Contacts export request successfully received! Check your email shortly",
      );
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    form.resetFields();
  }, [editData, form]);

  useEffect(() => {
    getAllContacts();
  }, []);

  return (
    <>
      <PageHeader
        ghost={false}
        title="Client Contacts"
        className={LayoutCss.appPageHeader}
      />

      <DataTableLayout
        searchDefaultValue={search}
        onSearchChange={setSearch}
        actions={[
          {
            renderer: () => (
              <Col>
                <ExportDataButton
                  onClick={onExportRequested}
                  disabled={clientContacts?.length === 0}
                />
              </Col>
            ),
          },
          {
            permissions: [K.Permissions.CreateClientContact],
            renderer: () => (
              <Col>
                <Button type="primary" onClick={showModal}>
                  Create
                </Button>
              </Col>
            ),
          },
        ]}
      >
        <ClientSideDataGrid
          ref={gridRef}
          userId={userId}
          configKey={K.AgGridTable.Keys.ClientContactsVisibleColumn}
          defaultColumnConfig={getColumns(
            userSlice.roles,
            editRecord,
            confirmDelete,
          )}
          rowData={clientContacts}
          quickFilterText={search}
        />
      </DataTableLayout>
      <Modal
        forceRender
        className="s2-theme-style"
        title={`${
          Object.keys(editData).length ? "Edit" : "Create"
        } Client Contact`}
        open={isModalVisible}
        centered
        closeIcon={<i className="icon-closeable" />}
        okText={`${Object.keys(editData).length ? "Update" : "Create"}`}
        wrapClassName="vertical-center-modal"
        onOk={handleOk}
        okButtonProps={{ loading: isLoading }}
        onCancel={handleCancel}
        afterClose={afterModalClose}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={editData}
        >
          <Form.Item
            label="Full Name"
            name="name"
            rules={[
              { required: true, message: "Full Name is required." },
              noTrailingSpaceAllowedRule(),
            ]}
          >
            <Input placeholder="Enter Full Name" />
          </Form.Item>
          <Form.Item
            label="Email Address"
            name="email"
            rules={[
              {
                required: true,
                message: "Email is required.",
              },
              {
                type: "email",
                message: "Enter a valid email address",
              },
            ]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>
          <Form.Item
            label="Phone Number"
            name="phoneNumber"
            rules={[
              {
                required: true,
                message: "Phone Number is required.",
              },
              {
                validator: (_, value) => {
                  if (!value || validateMobileNumber(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject("Please enter correct number");
                },
              },
            ]}
          >
            <PatternFormat
              placeholder="Enter Phone Number"
              customInput={Input}
              format={K.PhoneFormat.USNational}
            />
          </Form.Item>
          <Form.Item
            label="Phone Ext"
            name="phoneExt"
            rules={[
              {
                pattern: new RegExp("^[0-9]{1,6}$", "g"),
                message: "Maximum of 6 digits is allowed",
              },
            ]}
          >
            <Input placeholder="Enter Phone Ext" />
          </Form.Item>
          <Form.Item
            label="Job Title"
            name="jobTitle"
            rules={[
              {
                required: true,
                message: "Job title is required.",
              },
            ]}
          >
            <Input placeholder="Enter job title" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
